import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

import { CreateSlotRequest, Slot, createSlot as createSlotService } from '@website/services/slotManagement.service';

export function useSlotCreate() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const t = useTranslations('Slot.Create.feedback');

  const mutation = useMutation<Slot[], Error, CreateSlotRequest>({
    mutationFn: (slotData: CreateSlotRequest) => createSlotService(slotData),
    onSuccess: (data) => {
      // Success toast intentionally suppressed — creation feedback is visual
      // (the new slot appears); only failures surface a toast.
      // Invalidate relevant queries to refresh the data
      const activityId = data[0]?.activityId;
      if (activityId) {
        queryClient.invalidateQueries({ queryKey: ['activity', activityId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['activity'] });
      }
      queryClient.invalidateQueries({ queryKey: ['professional-activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['user-activities'] });

      // Refresh the server components to get the latest data
      router.refresh();
    },
    onError: (error: Error) => {
      toast.error(t('slotCreationFailedTitle'), {
        id: 'slot-create-error',
        description: error.message || t('slotCreationFailedDescription')
      });
    }
  });

  return mutation;
}
